"use client";

import { useState, useEffect, useCallback } from 'react';

// Define the available flags/countries in the system
export const availableCountries = [
  { name: "Global", flag: "🌐", code: "global", available: true, href: "/" },
  { name: "Nigeria", flag: "🇳🇬", code: "ng", available: true, href: "/" },
  { name: "South Africa", flag: "🇿🇦", code: "za", available: false },
  { name: "Ghana", flag: "🇬🇭", code: "gh", available: false },
  { name: "Kenya", flag: "🇰🇪", code: "ke", available: false },
];

// Default country to use as fallback
const DEFAULT_COUNTRY = availableCountries[0];

// Type for the country state
interface CountryState {
  loading: boolean;
  country: (typeof availableCountries)[number] | null;
  error: string | null;
}

// Types for ipapi.co response
interface IpApiResponse {
  ip: string;
  country_name: string;
  country_code: string;
  error?: boolean;
  reason?: string;
}

/**
 * Safely get a value from localStorage with error handling
 */
function safeGetFromStorage(key: string): string | null {
  if (typeof window === 'undefined') return null;
  
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return null;
  }
}

/**
 * Safely set a value in localStorage with error handling
 */
function safeSetInStorage(key: string, value: string): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.error(`Error setting ${key} in localStorage:`, error);
    return false;
  }
}

/**
 * Hook for detecting user's country based on IP and managing country selection
 */
export default function useCountryDetection() {
  // Initialize with safe SSR-compatible defaults only
  const [state, setState] = useState<CountryState>({
    loading: true,
    country: null,
    error: null
  });

  // Function to detect country using IP geolocation
  const detectCountry = useCallback(async (): Promise<(typeof availableCountries)[number]> => {
    try {
      // Fetch country data from ipapi.co
      const response = await fetch('https://ipapi.co/json/');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch country data: ${response.status}`);
      }
      
      const data: IpApiResponse = await response.json();
      
      if (data.error) {
        throw new Error(data.reason || 'Failed to detect country');
      }
      
      // Find matching country in our available countries list
      const countryCode = data.country_code.toLowerCase();
      console.log('Detected country code:', countryCode);
      
      const detectedCountry = availableCountries.find(
        c => c.code.toLowerCase() === countryCode && c.available
      );
      
      // Return detected country or fallback to Global
      return detectedCountry || DEFAULT_COUNTRY;
    } catch (error) {
      console.error('Error detecting country:', error);
      // Fallback to Global on error
      return DEFAULT_COUNTRY;
    }
  }, []);

  // Handle country selection and redirection
  const selectCountry = useCallback((country: (typeof availableCountries)[number]) => {
    console.log('Selecting country:', country.name);
    
    // Update state
    setState(prev => ({ ...prev, country, loading: false }));
    
    // Save to localStorage
    safeSetInStorage('selectedCountry', country.name);
    
    // Redirect if available and has href
    if (country.available && country.href) {
      window.location.href = country.href;
    }
  }, []);

  // Initialize country detection - check localStorage first, then detect if needed
  useEffect(() => {
    // Ensure we're in browser environment
    if (typeof window === 'undefined') return;

    const initCountry = async () => {
      try {
        // First check if we have a stored country preference
        const storedCountryName = safeGetFromStorage('selectedCountry');
        if (storedCountryName) {
          const storedCountry = availableCountries.find(c => c.name === storedCountryName);
          if (storedCountry) {
            setState({
              loading: false,
              country: storedCountry,
              error: null
            });
            return;
          }
        }

        // If no stored country, detect using IP
        console.log('Detecting country...');
        const detectedCountry = await detectCountry();
        console.log('Setting detected country:', detectedCountry.name);
        
        // Set the detected country in state and localStorage
        setState({
          loading: false,
          country: detectedCountry,
          error: null,
        });
        
        safeSetInStorage('selectedCountry', detectedCountry.name);
      } catch (error) {
        console.error('Error initializing country:', error);
        
        // Fallback to Global on error
        setState({
          loading: false,
          country: DEFAULT_COUNTRY,
          error: 'Failed to detect country',
        });
        
        safeSetInStorage('selectedCountry', DEFAULT_COUNTRY.name);
      }
    };
    
    initCountry();
  }, [detectCountry]);

  return {
    ...state,
    selectCountry,
    availableCountries,
  };
} 